"use client";

import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { login as loginRequest, logout as logoutRequest, refreshAccess, verifyToken } from "./client";
import { clearSession, readSession, writeSession, type SessionTokens } from "./session";

type AuthStatus = "loading" | "anonymous" | "authenticated";

interface AuthContextValue {
  status: AuthStatus;
  username: string | null;
  login: (username: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<AuthStatus>("loading");
  const [username, setUsername] = useState<string | null>(null);
  const [tokens, setTokens] = useState<SessionTokens | null>(null);

  useEffect(() => {
    const stored = readSession();
    if (!stored) {
      queueMicrotask(() => setStatus("anonymous"));
      return;
    }

    let cancelled = false;
    restore(stored)
      .then((session) => {
        if (cancelled) return;
        if (!session) {
          clearSession();
          setTokens(null);
          setUsername(null);
          setStatus("anonymous");
          return;
        }
        writeSession(session.tokens);
        setTokens(session.tokens);
        setUsername(session.username);
        setStatus("authenticated");
      })
      .catch(() => {
        if (cancelled) return;
        clearSession();
        setTokens(null);
        setUsername(null);
        setStatus("anonymous");
      });

    return () => {
      cancelled = true;
    };
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      status,
      username,

      async login(nextUsername, password) {
        const session = await loginRequest(nextUsername, password);
        writeSession(session);
        setTokens(session);
        setUsername(nextUsername);
        setStatus("authenticated");
      },

      async logout() {
        const current = tokens;
        clearSession();
        setTokens(null);
        setUsername(null);
        setStatus("anonymous");
        if (!current) return;
        try {
          await logoutRequest(current);
        } catch {
          // The browser session is already cleared.
        }
      },
    }),
    [status, tokens, username],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const value = useContext(AuthContext);
  if (!value) throw new Error("useAuth must be used within AuthProvider");
  
  return value;
}

async function restore(stored: SessionTokens) {
  const access = await verifyToken(stored.access);
  if (access) return { username: access.sub, tokens: stored };

  const username = readSubject(stored.access);
  try {
    const accessToken = await refreshAccess(stored.refresh);
    return {
      username: username ?? readSubject(accessToken),
      tokens: { access: accessToken, refresh: stored.refresh },
    };
  } catch {
    return null;
  }
}

function readSubject(token: string) {
  const segment = token.split(".")[1];
  if (!segment) return null;
  try {
    const payload = JSON.parse(decodeBase64Url(segment)) as { sub?: unknown };
    return typeof payload.sub === "string" && payload.sub.length > 0 ? payload.sub : null;
  } catch {
    return null;
  }
}

function decodeBase64Url(segment: string) {
  const base64 = segment.replace(/-/g, "+").replace(/_/g, "/");
  const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), "=");
  return atob(padded);
}
